import type {
  ProgressLedgerEntry,
  QuestHealthDay,
  QuestProgress,
  QuestRoute,
  QuestState,
  RouteLandmark,
} from "./types";
import { fnv1aHash } from "./stableJson";

export const DEFAULT_STRIDE_METERS = 0.762;

export function createEmptyQuestState(
  routeCatalogVersion: string,
  nowIso: string,
): QuestState {
  return {
    schemaVersion: 1,
    routeCatalogVersion,
    activeQuest: null,
    ledger: [],
    unlockedLandmarkIds: [],
    openedChestIds: [],
    earnedBadges: [],
    preferences: {},
    updatedAt: nowIso,
  };
}

export function metersForHealthDay(
  route: QuestRoute,
  day: QuestHealthDay,
): number {
  if (
    route.progressPolicy.movementSource === "distance" &&
    day.distanceMeters > 0
  ) {
    return applyDailyCap(route, day.distanceMeters);
  }
  if (day.steps === 0 && day.distanceMeters > 0) {
    return applyDailyCap(route, day.distanceMeters);
  }
  return applyDailyCap(
    route,
    day.steps * (route.progressPolicy.strideMeters || DEFAULT_STRIDE_METERS),
  );
}

export function restedBonusXp(day: QuestHealthDay): number {
  const minutes = day.sleep?.minutesAsleep ?? 0;
  if (minutes >= 480) return 50;
  if (minutes >= 420) return 25;
  return 0;
}

export function xpForHealthDay(day: QuestHealthDay): number {
  return (
    Math.floor(
      (day.steps > 0
        ? day.steps
        : day.distanceMeters / DEFAULT_STRIDE_METERS) / 100,
    ) +
    day.activeZoneMinutes.total * 2 +
    restedBonusXp(day)
  );
}

export function healthSnapshotHash(day: QuestHealthDay): string {
  return fnv1aHash({
    date: day.date,
    steps: day.steps,
    distanceMeters: day.distanceMeters,
    activeZoneMinutes: day.activeZoneMinutes,
    sleep: day.sleep
      ? {
          minutesAsleep: day.sleep.minutesAsleep,
          minutesAwake: day.sleep.minutesAwake,
          minutesInSleepPeriod: day.sleep.minutesInSleepPeriod,
        }
      : null,
  });
}

export function createLedgerEntry(params: {
  route: QuestRoute;
  day: QuestHealthDay;
  assignmentReason: ProgressLedgerEntry["assignmentReason"];
  createdAt: string;
  supersedesEntryId?: string | null;
}): ProgressLedgerEntry {
  const snapshotHash = healthSnapshotHash(params.day);
  return {
    entryId: `${params.route.routeVersionId}:${params.day.date}:${snapshotHash}`,
    sourceDate: params.day.date,
    healthSnapshotHash: snapshotHash,
    steps: params.day.steps,
    azm: params.day.activeZoneMinutes.total,
    sleepMinutes: params.day.sleep?.minutesAsleep ?? null,
    meters: metersForHealthDay(params.route, params.day),
    xp: xpForHealthDay(params.day),
    assignedRouteVersionId: params.route.routeVersionId,
    assignmentReason: params.assignmentReason,
    spentForCompletionId: null,
    supersedesEntryId: params.supersedesEntryId ?? null,
    createdAt: params.createdAt,
  };
}

export function upsertDailyLedgerEntry(
  ledger: ProgressLedgerEntry[],
  entry: ProgressLedgerEntry,
): ProgressLedgerEntry[] {
  const activeForDate = latestLedgerEntries(ledger).find(
    (existing) =>
      existing.sourceDate === entry.sourceDate &&
      existing.assignedRouteVersionId === entry.assignedRouteVersionId,
  );
  if (activeForDate?.healthSnapshotHash === entry.healthSnapshotHash) {
    return ledger;
  }
  const correctedEntry = activeForDate
    ? { ...entry, supersedesEntryId: activeForDate.entryId }
    : entry;
  return [...ledger, correctedEntry];
}

export function latestLedgerEntries(
  ledger: ProgressLedgerEntry[],
): ProgressLedgerEntry[] {
  const superseded = new Set(
    ledger
      .map((entry) => entry.supersedesEntryId)
      .filter((entryId): entryId is string => Boolean(entryId)),
  );
  return ledger.filter((entry) => !superseded.has(entry.entryId));
}

export function deriveQuestProgress(
  route: QuestRoute,
  state: QuestState,
): QuestProgress {
  const entries = latestLedgerEntries(state.ledger).filter(
    (entry) => entry.assignedRouteVersionId === route.routeVersionId,
  );
  const meters = sum(entries.map((entry) => entry.meters));
  const xp = sum(entries.map((entry) => entry.xp));
  const landmarks = routeLandmarks(route);
  const percentComplete = clamp(
    routeDistanceMeters(route) > 0
      ? (meters / routeDistanceMeters(route)) * 100
      : 0,
    0,
    100,
  );
  const unlockedLandmarkIds = landmarks
    .filter((landmark) => landmarkIsUnlocked(route, landmark, meters))
    .map((landmark) => landmark.landmarkId);
  const unlockedRewardIds = route.rewards
    .filter((reward) => {
      if (reward.unlock.completion) return percentComplete >= 100;
      if (typeof reward.unlock.atMeters === "number") {
        return meters >= reward.unlock.atMeters;
      }
      if (typeof reward.unlock.atPercent === "number") {
        return percentComplete >= reward.unlock.atPercent;
      }
      return false;
    })
    .map((reward) => reward.rewardId);
  const nextLandmark =
    landmarks.find(
      (landmark) => !unlockedLandmarkIds.includes(landmark.landmarkId),
    ) ?? null;

  return {
    routeVersionId: route.routeVersionId,
    meters,
    xp,
    level: levelForXp(xp),
    percentComplete,
    unlockedLandmarkIds,
    unlockedRewardIds,
    nextLandmark,
  };
}

export function levelForXp(xp: number): number {
  return Math.max(1, Math.floor(Math.sqrt(Math.max(0, xp) / 100)) + 1);
}

export function questLogLine(day: QuestHealthDay): string {
  if (day.steps <= 1_000) {
    return "You lingered at camp, studied the map, and enjoyed the view from your current stop.";
  }
  if ((day.sleep?.minutesAsleep ?? 0) >= 480 && day.activeZoneMinutes.total >= 60) {
    return "A full rest made the morning path feel bright, and you found extra energy for a scenic detour.";
  }
  if (day.activeZoneMinutes.total >= 60 || day.steps >= 12_000) {
    return "You crossed sunlit ground with strong momentum and found a new view along the way.";
  }
  if ((day.sleep?.minutesAsleep ?? 0) >= 420) {
    return "A good rest made the day feel easy, with quiet progress under a clear sky.";
  }
  return "You made steady progress and collected a small story from the trail.";
}

function landmarkIsUnlocked(
  route: QuestRoute,
  landmark: RouteLandmark,
  meters: number,
): boolean {
  if (typeof landmark.atMeters === "number") return meters >= landmark.atMeters;
  if (typeof landmark.atPercent === "number") {
    return (meters / routeDistanceMeters(route)) * 100 >= landmark.atPercent;
  }
  return false;
}

export function routeLandmarks(route: QuestRoute): RouteLandmark[] {
  if ("landmarks" in route) return route.landmarks;
  if ("stages" in route) {
    return route.stages.flatMap((stage) => stage.landmarks);
  }
  if ("checkpoints" in route) {
    return route.checkpoints.map((checkpoint, index) => ({
      landmarkId: checkpoint.checkpointId,
      title: checkpoint.title,
      description: `${checkpoint.title} collection stop.`,
      atPercent:
        route.checkpoints.length <= 1
          ? 100
          : (index / (route.checkpoints.length - 1)) * 100,
    }));
  }
  return [];
}

export function routeDistanceMeters(route: QuestRoute): number {
  return route.gameDistanceMeters ?? route.progressPolicy.gameDistanceMeters ?? 0;
}

function applyDailyCap(route: QuestRoute, meters: number): number {
  const cap = route.progressPolicy.dailyCapMeters;
  return typeof cap === "number" ? Math.min(meters, cap) : meters;
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
